"use client";

import Script from "next/script";
import { useEffect, useState } from "react";
import { getPathname } from "@/i18n/navigation";
import { links, defaultLocale, type Locale } from "@/config";


const OneLinkUrl = process.env.NEXT_PUBLIC_APPSFLYER_ONELINK_URL;
const DefaultMediaSource = process.env.NEXT_PUBLIC_APPSFLYER_DEFAULT_MEDIA_SOURCE;
const ConsentUpdateEvent = "appsflyer-consent-update";

const mediaSource = {
  defaultValue: DefaultMediaSource!,

  keys: [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
  ],
};

const customSsUi = {
  paramKey: "af_ss_ui",
  defaultValue: "true"
};


declare global {
  interface Window {
    AF_CONSENT?: boolean;

    AF_SMART_SCRIPT?: {
      generateQRCode: (params: {
        url: string;
        container: HTMLElement;
      }) => void;

      generateOneLinkURL: (params: {
        oneLinkURL: string;
        afParameters: {
          mediaSource: { keys: string[]; defaultValue: string };
          afCustom: { paramKey: string; defaultValue: string }[];
        };
      }) => {
        clickURL: string;
      };
    };
  }
}


export function useDownloadUrl(locale?: Locale) {
  const deviceOs = useDeviceOs();
  const appsFlyer = useAppsFlyer();

  const [oneLinkUrl, setOneLinkUrl] = useState<string | null>(null);
  const [directUrl, setDirectUrl] = useState<string | null>(null);
  const [fallbackUrl] = useState(() => {
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
    const pathname = getPathname({ locale: locale ?? defaultLocale, href: "/download" });
    return `${baseUrl}${pathname}`;
  });

  useEffect(() => {
    if (appsFlyer.ready && !oneLinkUrl) {
      setOneLinkUrl(appsFlyer.generateOneLinkUrl());
    }
  }, [appsFlyer.ready, oneLinkUrl]);

  useEffect(() => {
    if (deviceOs && !directUrl) {
      setDirectUrl(
        deviceOs === "android"
          ? links.download.playStore
          : links.download.appStore
      );
    }
  }, [deviceOs, directUrl]);

  return (
    oneLinkUrl ?? directUrl ?? fallbackUrl
  );
}


export function useAppsFlyer() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const updateState = () => {
      setReady(!!window.AF_CONSENT && !!window.AF_SMART_SCRIPT);
    }

    document.addEventListener(ConsentUpdateEvent, updateState);

    updateState();

    return () => {
      document.removeEventListener(ConsentUpdateEvent, updateState);
    };
  }, []);

  const generateOneLinkUrl = () => {
    if (!ready || !window.AF_SMART_SCRIPT) {
      return "";
    }

    const result = window.AF_SMART_SCRIPT.generateOneLinkURL({
      oneLinkURL: OneLinkUrl!,
      afParameters: {
        mediaSource: mediaSource,
        afCustom: [customSsUi],
      },
    });

    return result.clickURL;
  };

  const generateQrCode = (container: HTMLElement, url: string) => {
    if (!ready || !window.AF_SMART_SCRIPT) {
      return;
    }

    window.AF_SMART_SCRIPT.generateQRCode({
      url,
      container
    });
  };

  if (!ready) {
    return {
      ready: false as const,
    };
  }

  return {
    ready: true as const,
    generateOneLinkUrl,
    generateQrCode,
  };
}


export function updateAppsFlyerConsent(consentGranted: boolean) {
  window.AF_CONSENT = consentGranted;
  document.dispatchEvent(new Event(ConsentUpdateEvent));
}


export function AppsFlyerProvider() {
  if (!OneLinkUrl || !DefaultMediaSource) {
    return null;
  }

  return (
    <Script
      src="https://onelinksmartscript.appsflyer.com/onelink-smart-script-latest.js"
      strategy="afterInteractive"
      onLoad={() => {
        document.dispatchEvent(new Event(ConsentUpdateEvent));
      }}
    />
  );
}


function useDeviceOs() {
  const [deviceOs, setDeviceOs] = useState<"ios" | "android" | undefined>(undefined);

  useEffect(() => {
		const userAgent = navigator.userAgent || (navigator as any).vendor || (window as any).opera;

		if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
			setDeviceOs("ios");
		}

		else if (/android/i.test(userAgent)) {
			setDeviceOs("android");
		}
  }, []);

  return deviceOs;
}
